import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { copyTextToClipboard } from "@/utils/clipboard";
import { flowActionErrorMessage } from "./flowActionError";
import { flowDetailQuery } from "./queries";

const COPIED_FEEDBACK_MS = 2000;

export type CopyFlowLinkState =
  | { status: "idle" | "pending" | "copied" }
  | { status: "error"; message: string };

/**
 * Copies a flow's link. The S5 list never carries it, so it comes from S6 on
 * demand; card and modal share this hook and the S6 cache entry.
 */
export function useCopyFlowLink(borrowerId: number, flowId: number) {
  const queryClient = useQueryClient();
  const [state, setState] = useState<CopyFlowLinkState>({ status: "idle" });
  const resetTimer = useRef<number>();

  useEffect(() => () => window.clearTimeout(resetTimer.current), []);

  const copy = useCallback(async () => {
    window.clearTimeout(resetTimer.current);
    setState({ status: "pending" });
    try {
      // `link_url` is fixed for the life of a flow; resend reuses the token.
      const { link_url } = await queryClient.fetchQuery({
        ...flowDetailQuery(borrowerId, flowId),
        staleTime: Infinity,
      });
      await copyTextToClipboard(link_url);
      setState({ status: "copied" });
      resetTimer.current = window.setTimeout(
        () => setState({ status: "idle" }),
        COPIED_FEEDBACK_MS
      );
    } catch (err) {
      setState({ status: "error", message: flowActionErrorMessage(err) });
    }
  }, [queryClient, borrowerId, flowId]);

  return { copy, state };
}
